'use client';

import React, { useEffect, useRef } from 'react';
import Link from 'next/link';
import AppImage from '@/components/ui/AppImage';

export default function GalaNightFeature() {
  const sectionRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {entries.forEach((e) => {if (e.isIntersecting) e.target.classList.add('active');});},
      { threshold: 0.08 }
    );
    sectionRef?.current?.querySelectorAll('.reveal, .reveal-left, .reveal-right')?.forEach((el) => observer?.observe(el));
    return () => observer?.disconnect();
  }, []);

  return (
    <section ref={sectionRef} className="bg-dark-bg noise-overlay w-full px-4 md:px-12 pt-14 md:pt-20 pb-12 md:pb-16">
      <div className="max-w-7xl mx-auto">
        <div className="text-center mb-10 md:mb-14 reveal">
          <span className="inline-block px-4 py-1.5 rounded-full border border-accent/30 text-accent text-xs font-bold tracking-widest uppercase mb-4 md:mb-5">
            Tuesday, 20 October 2026
          </span>
          <h2 className="font-display text-3xl md:text-4xl lg:text-6xl font-black text-white tracking-tight leading-tight">
            The Gala Evening
          </h2>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 md:gap-8 items-stretch">
          {/* Artist Feature */}
          <div className="relative overflow-hidden rounded-3xl md:rounded-4xl min-h-[340px] md:min-h-[420px] reveal-left group">
            <AppImage
              src="/assets/images/javed-ali.png"
              alt="Javed Ali performing live on stage with dramatic red lighting"
              fill
              className="object-cover grayscale-hover"
              sizes="(max-width: 1024px) 100vw, 50vw" />
            
            <div className="absolute inset-0 bg-gradient-to-t from-dark-bg/95 via-dark-bg/40 to-transparent" />
            <div className="absolute inset-0 flex flex-col justify-between p-6 md:p-8">
              <span className="self-start px-3 md:px-4 py-1.5 md:py-2 rounded-full bg-primary text-primary-foreground text-xs font-bold tracking-widest uppercase">
                Exclusive for Members
              </span>
              <div>
                <p className="text-accent text-xs font-bold tracking-widest uppercase mb-2">Headline Artist · 20 October 2026</p>
                <h3 className="font-display text-4xl md:text-5xl font-black text-white tracking-tighter mb-2 md:mb-3">Javed Ali</h3>
                <p className="text-white/70 text-sm leading-relaxed mb-2">
                  Kun Faaya Kun · Dil Ibaadat · Ek Din Teri Raahon Mein
                </p>
                <p className="text-white/50 text-xs">Live at Shanmukhananda Chandrasekhara Saraswati Auditorium · 7 PM onwards</p>
              </div>
            </div>
          </div>

          {/* Venue Feature */}
          <div className="flex flex-col gap-4 md:gap-5 reveal-right">
            <div className="bg-dark-card border border-accent/15 rounded-3xl md:rounded-4xl p-6 md:p-8 flex-1">
              <div className="flex items-center gap-3 mb-4 md:mb-6">
                <div className="w-10 h-10 md:w-12 md:h-12 rounded-full bg-accent/15 flex items-center justify-center text-xl md:text-2xl flex-shrink-0">🏛️</div>
                <div>
                  <h3 className="font-display text-lg md:text-xl font-black text-white tracking-tight">Shanmukhananda Chandrasekhara Saraswati Auditorium</h3>
                  <p className="text-white/50 text-xs">Sion East, Mumbai</p>
                </div>
              </div>
              <p className="text-white/60 text-sm leading-relaxed mb-4 md:mb-6">
                Inaugurated 1963 - India&apos;s largest and most iconic concert hall. Renowned for world-class acoustics and traditional Indian temple architecture. Past performers include Kumar Sanu, Adnan Sami, Papon, and Piyush Mishra.
              </p>
              <div className="grid grid-cols-3 gap-3 md:gap-4 mb-4 md:mb-6">
                {[
                { num: '2,763', label: 'Seated Capacity' },
                { num: '1963', label: 'Inaugurated' },
                { num: '3', label: 'Floors' }]?.
                map((s) =>
                <div key={s?.label} className="text-center">
                    <p className="font-display text-xl md:text-2xl font-black text-accent">{s?.num}</p>
                    <p className="text-white/40 text-xs mt-1">{s?.label}</p>
                  </div>
                )}
              </div>
              <div className="flex items-start gap-3 text-white/50 text-xs">
                <svg className="flex-shrink-0 mt-0.5 text-accent/60" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M20 10c0 6-8 12-8 12s-8-6-8-12a8 8 0 0 1 16 0Z" /><circle cx="12" cy="10" r="3" />
                </svg>
                Harbanslal Marg, Sion East, Mumbai · ~1 km from Kings Circle station (Central & Harbour lines)
              </div>
            </div>

            <div className="bg-dark-card border border-accent/15 rounded-2xl md:rounded-3xl p-5 md:p-6">
              <p className="text-white/40 text-xs font-bold tracking-widest uppercase mb-3 md:mb-4">Access by Membership Tier</p>
              <div className="space-y-2.5 md:space-y-3">
                {[
                { tier: 'Gold', access: 'General Seating' },
                { tier: 'Diamond', access: 'Preferred Seating' },
                { tier: 'Platinum', access: 'Front Row / VIP' }]?.
                map((item) =>
                <div key={item?.tier} className="flex items-center justify-between">
                    <span className="text-white/70 text-sm font-semibold">{item?.tier}</span>
                    <span className="text-accent/80 text-xs font-medium">{item?.access}</span>
                  </div>
                )}
              </div>
              <div className="mt-4 md:mt-5 pt-4 md:pt-5 border-t border-white/10">
                <Link
                  href="/membership"
                  className="block text-center py-3 px-6 rounded-full bg-primary text-primary-foreground font-bold text-sm hover:bg-secondary transition-colors duration-200">
                  
                  Secure Your Gala Pass
                </Link>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>);

}